import type { OhlcvTuple } from './types'
export type Tick = [number, number] // [timestamp, price]

export class TickUtils {
  /**
   * Заполняет «дырки» в тиках (разрывы > 1 секунды)
   */
  static fixTicks(ticks: Tick[], fromTs?: number): Tick[] {
    if (!ticks.length) return []

    let firstOcc = 0
    // fromTs
    if (fromTs) {
      firstOcc = ticks.findIndex((v) => {
        return v[0] >= fromTs
      })
      if (firstOcc == -1) {
        return []
      }
    }

    const filled: Tick[] = []
    let prevT = ticks[firstOcc]?.[0]
    let prevP = ticks[firstOcc]?.[1]
    if (!prevT || !prevP) {
      return []
    }

    filled.push([prevT, prevP])

    for (let i = firstOcc + 1; i < ticks.length; i++) {
      const curT = ticks[i]?.[0]
      const curP = ticks[i]?.[1]

      if (!curT || !curP) {
        continue
      }

      while (prevT + 1 < curT) {
        prevT++
        filled.push([prevT, prevP])
      }

      filled.push([curT, curP])
      prevT = curT
      prevP = curP
    }

    return filled
  }

  /**
   * Добивает серию тиков до указанного времени
   */
  static fillTicksTo(ticks: Tick[], endTime: number, fromTs?: number): Tick[] {
    const filled = this.fixTicks(ticks, fromTs)
    if (!filled || !filled.length) return []

    let lastT = filled[filled.length - 1]?.[0]
    const lastP = filled[filled.length - 1]?.[1]

    if (!lastT || !lastP) {
      return []
    }

    while (lastT < endTime) {
      lastT++
      filled.push([lastT, lastP])
    }
    return filled
  }

  /**
   * Считает спрэд (в %) между двумя сериями тиков на общих метках времени
   */
  static calcSpreads(ticksA: Tick[], ticksB: Tick[]): Tick[] {
    const idxA = new Map(ticksA.map((c, i) => [c[0], i]))
    const idxB = new Map(ticksB.map((c, i) => [c[0], i]))

    const commonTs = [...idxA.keys()].filter((ts) => idxB.has(ts)).sort((x, y) => x - y)

    return commonTs.map((ts) => {
      const bp = ticksA[idxA.get(ts)!]?.[1]
      const sp = ticksB[idxB.get(ts)!]?.[1]
      if (!bp || !sp) {
        throw 'bp/sp error'
      }
      return [ts, Number(((sp / bp - 1) * 100).toFixed(2))]
    })
  }

  /**
   * Агрегирует тики в OHLC-свечи указанного таймфрейма (в минутах)
   */
  static ticksToOhlcv(ticks: Tick[], timeframeSec = 60): OhlcvTuple[] {
    if (!ticks.length) return []

    const ms = timeframeSec
    const buckets = new Map<number, { o: number; h: number; l: number; c: number }>()

    for (const [t, price] of ticks) {
      const openT = Math.floor(t / ms) * ms
      if (!buckets.has(openT)) {
        buckets.set(openT, { o: price, h: price, l: price, c: price })
      }
      const b = buckets.get(openT)!
      b.h = Math.max(b.h, price)
      b.l = Math.min(b.l, price)
      b.c = price
    }

    return Array.from(buckets.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([t, { o, h, l, c }]) => [t, o, h, l, c])
  }
  static calcSpread = (x: Tick[], y: Tick[]) => {
    const xMap = new Map()
    for (let i = 0; i < x.length; i++) {
      const ts = x[i]?.[0]
      const value = x[i]?.[1]
      xMap.set(Number(ts), value)
    }
    const result = []
    for (const [ts, price] of y) {
      if (xMap.has(ts)) {
        const spread = (price / xMap.get(ts) - 1) * 100
        result.push([ts, Number(Number(spread).toFixed(2))])
      }
    }
    return result as Tick[]
  }

  static updateTicks(existing: Tick[] = [], newData: Tick[] = []): Tick[] {
    // Если один из массивов пустой — сразу возвращаем другой
    if (!existing.length) return newData
    if (!newData.length) return existing

    // Сливаем массивы и создаём Map (автоматически удаляет дубликаты, оставляя последние)
    const merged = new Map<number, number>([...existing, ...newData])

    // Возвращаем отсортированный по времени результат
    return Array.from(merged.entries()).sort((a, b) => a[0] - b[0])
  }
}
